import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';

import { isChangeManager } from '../changePermissions';
import { useAuth } from '../../../context/AuthContext';
import { useModal } from '../../../context/ModalContext';
import { changesApi } from '../../../services/changesApi';
import type { ChangeSapRequestTransportSettings, ChangeWorkGroup } from '../../../types/changes';
import { getApiErrorMessage } from '../../../utils/apiError';
import { useActiveUsers } from '../hooks/useActiveUsers';

const emptySettings = (): ChangeSapRequestTransportSettings => ({
  is_enabled: false,
  group_id: null,
  assignee_id: null,
});

const ChangeSapRequestTransportPanel: React.FC = () => {
  const { user } = useAuth();
  const { showAlert } = useModal();
  const canManage = isChangeManager(user);
  const { users } = useActiveUsers({ limit: 80, enabled: canManage });
  const [settings, setSettings] = useState<ChangeSapRequestTransportSettings>(emptySettings());
  const [areas, setAreas] = useState<ChangeWorkGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [next, groupsRes] = await Promise.all([
        changesApi.getSapRequestTransportSettings(),
        changesApi.listWorkGroups({ only_active: true }).catch(() => ({ items: [] })),
      ]);
      setSettings(next);
      setAreas(groupsRes.items || []);
      setError(null);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao carregar aprovação SAP (request).'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const areaOptions = useMemo(
    () => [...areas].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR')),
    [areas],
  );

  const save = async () => {
    if (!canManage) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await changesApi.replaceSapRequestTransportSettings({
        is_enabled: settings.is_enabled,
        group_id: settings.group_id,
        assignee_id: settings.assignee_id,
      });
      setSettings(saved);
      showAlert({
        title: 'Transporte SAP (request)',
        message: 'Configuração salva. Mudanças SAP com «Tem request?» = Sim incluirão esta aprovação.',
        variant: 'success',
      });
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível salvar.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-muted small">Carregando…</p>;
  }

  return (
    <div>
      <p className="text-muted small">
        Quando a mudança for <strong>SAP</strong> e o solicitante responder <strong>Sim</strong> em «Tem
        request?», entra um slot extra de <strong>Transporte</strong> além de GTEC/GSERV-I (e demais áreas
        fixas). Escolha a área e, se quiser, a pessoa que decide (como no plano de aprovação).
      </p>
      {error ? (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      <Form.Check
        type="switch"
        id="sap-request-transport-enabled"
        className="mb-3"
        label="Ativar aprovação extra de Transporte para SAP com request"
        checked={settings.is_enabled}
        disabled={!canManage}
        onChange={(event) =>
          setSettings((prev) => ({ ...prev, is_enabled: event.target.checked }))
        }
      />
      <Form.Group className="mb-3">
        <Form.Label>Área de mudança aprovadora</Form.Label>
        <Form.Select
          aria-label="Área aprovadora SAP request"
          value={settings.group_id ?? ''}
          disabled={!canManage}
          onChange={(event) => {
            const value = event.target.value;
            setSettings((prev) => ({
              ...prev,
              group_id: value ? Number(value) : null,
            }));
          }}
        >
          <option value="">Selecione a área</option>
          {areaOptions.map((area) => (
            <option key={area.group_id} value={area.group_id}>
              {area.name}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Pessoa (opcional)</Form.Label>
        <Form.Select
          aria-label="Pessoa aprovadora SAP request"
          value={settings.assignee_id ?? ''}
          disabled={!canManage}
          onChange={(event) => {
            const value = event.target.value;
            setSettings((prev) => ({
              ...prev,
              assignee_id: value ? Number(value) : null,
            }));
          }}
        >
          <option value="">Qualquer membro da área</option>
          {users.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name || item.email}
            </option>
          ))}
        </Form.Select>
        <Form.Text className="text-muted">
          Com pessoa definida, só ela pode aprovar ou reprovar este slot (padrão do Transporte).
        </Form.Text>
      </Form.Group>
      {canManage ? (
        <Button disabled={saving} onClick={() => void save()}>
          {saving ? 'Salvando…' : 'Salvar'}
        </Button>
      ) : (
        <p className="small text-muted mb-0">Somente gestor de mudanças pode alterar este cadastro.</p>
      )}
    </div>
  );
};

export default ChangeSapRequestTransportPanel;
